import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRightIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon, MapPinIcon, RocketLaunchIcon,
} from '@heroicons/react/24/outline'
import { useSamouczek } from '../../context/SamouczekContext'
import { czasKroku, krokPo, powodCzekania } from '../../lib/samouczek/kroki'
import { ikonaKroku } from './ikonyKrokow'

/**
 * Panel samouczka „Pierwsze kroki” w prawym dolnym rogu (na telefonie na dole ekranu): bieżący krok z opisem,
 * ścieżką „Gdzie”, przyciskiem „Pokaż mi” (przejście na stronę i podświetlenie miejsca), „Pomiń ten krok”,
 * lista wszystkich kroków i „Pomiń samouczek”. Zwija się do pigułki z postępem; zwinięty jest też na czas
 * podświetlenia, żeby nie zasłaniał podświetlonego miejsca. Warstwa `z-[45]`: nad dymkiem „Zgłoś błąd” (z-40),
 * który na telefonie wchodzi w dół panelu, a pod oknami (z-50).
 */

const KOLOR_ODCINKA = { zrobiony: 'bg-emerald-500', pominiety: 'bg-gray-300', czeka: 'bg-gray-200', otwarty: 'bg-gray-200' }

const Postep = ({ kroki, biezacy }) => (
  <div className="flex gap-1" aria-hidden="true">
    {kroki.map(({ krok, stan }) => (
      <span key={krok.klucz}
            className={`h-1.5 flex-1 rounded-full ${stan !== 'zrobiony' && krok.klucz === biezacy ? 'bg-blue-500' : KOLOR_ODCINKA[stan]}`} />
    ))}
  </div>
)

const PrzyciskGlowny = ({ onClick, children }) => (
  <button type="button" onClick={onClick}
          className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
    {children}
  </button>
)
const PrzyciskDrugi = ({ onClick, children }) => (
  <button type="button" onClick={onClick}
          className="rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm font-semibold text-gray-700 hover:border-gray-300 hover:bg-gray-50">
    {children}
  </button>
)
const PrzyciskTekst = ({ onClick, children }) => (
  <button type="button" onClick={onClick} className="rounded-xl px-2.5 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50 hover:text-gray-800">
    {children}
  </button>
)

const Krok = () => {
  const { stan, fakty, pamiec, pokaz, pominKrok, potwierdz, dalej, wybierzKrok } = useSamouczek()
  const navigate = useNavigate()
  const { krok, stan: stanKroku } = stan.biezacy
  const nr = stan.kroki.findIndex((k) => k.krok.klucz === krok.klucz) + 1
  const Ikona = ikonaKroku(krok.klucz)

  if (stanKroku === 'zrobiony') {
    const nastepny = stan.kroki.find((k) => k.krok.klucz === krokPo(krok.klucz, fakty, pamiec))
    return (
      <div className="px-4 pb-4 pt-3" aria-live="polite">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
            <CheckIcon className="h-5 w-5" strokeWidth={2.6} />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold text-emerald-700">Zrobione</p>
            <h3 className="text-base font-bold text-gray-900">{krok.tytul}</h3>
          </div>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-gray-600">{krok.gotowe}</p>
        <div className="mt-4">
          <PrzyciskGlowny onClick={dalej}>
            {nastepny ? <>Dalej: {nastepny.krok.tytul}</> : 'Podsumowanie'} <ArrowRightIcon className="h-4 w-4" />
          </PrzyciskGlowny>
        </div>
      </div>
    )
  }

  const powod = powodCzekania(krok, fakty)
  const zadania = krok.zadania ? krok.zadania(fakty) : null
  return (
    <div className="px-4 pb-4 pt-3">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
          <Ikona className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <p className="text-xs font-semibold text-blue-700">Krok {nr} z {stan.wszystkich} · ok. {czasKroku(krok.minut)}</p>
          <h3 className="text-base font-bold leading-snug text-gray-900">{krok.tytul}</h3>
        </div>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{krok.opis(fakty)}</p>

      {zadania && (
        <ul className="mt-3 space-y-1.5">
          {zadania.map((z) => (
            <li key={z.tekst} className="flex items-center gap-2 text-sm">
              {'zrobione' in z ? (
                <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${z.zrobione ? 'bg-emerald-600 text-white' : 'border-2 border-gray-300'}`}>
                  {z.zrobione && <CheckIcon className="h-3 w-3" strokeWidth={3} />}
                </span>
              ) : <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-blue-600" aria-hidden="true" />}
              {z.adres
                ? <button type="button" onClick={() => navigate(z.adres)} className="font-semibold text-blue-700 hover:underline">{z.tekst}</button>
                : <span className={z.zrobione ? 'text-gray-500 line-through decoration-gray-300' : 'text-gray-800'}>{z.tekst}</span>}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 flex items-start gap-1.5 text-xs text-gray-500">
        <MapPinIcon className="mt-px h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span><span className="font-semibold text-gray-600">Gdzie:</span> {krok.gdzie.join(' › ')}</span>
      </p>
      {powod && (
        <p className="mt-2 flex items-start gap-1.5 text-xs font-medium text-amber-700">
          <ClockIcon className="mt-px h-4 w-4 flex-shrink-0" aria-hidden="true" />{powod}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {powod ? (
          // Na konto bez firmy czekają wszystkie kroki (najpierw NIP), na pierwszego klienta czeka tylko kalendarz.
          <PrzyciskGlowny onClick={() => wybierzKrok(fakty.firmowe && krok.klucz === 'kalendarz' ? 'klienci' : 'firma')}>
            {fakty.firmowe && krok.klucz === 'kalendarz' ? 'Przejdź do kroku Klienci' : 'Przejdź do kroku 1'}
          </PrzyciskGlowny>
        ) : (
          <PrzyciskGlowny onClick={() => pokaz(krok.klucz)}>Pokaż mi <ArrowRightIcon className="h-4 w-4" /></PrzyciskGlowny>
        )}
        {krok.potwierdzenie && !powod && <PrzyciskDrugi onClick={() => potwierdz(krok.klucz)}>{krok.potwierdzenie}</PrzyciskDrugi>}
        <PrzyciskTekst onClick={() => pominKrok(krok.klucz)}>Pomiń ten krok</PrzyciskTekst>
      </div>
    </div>
  )
}

const Podsumowanie = () => {
  const { stan, zakoncz } = useSamouczek()
  const navigate = useNavigate()
  const pominiete = stan.kroki.filter((k) => k.stan === 'pominiety').map((k) => k.krok.tytul)
  return (
    <div className="px-4 pb-4 pt-3" aria-live="polite">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
          <CheckIcon className="h-5 w-5" strokeWidth={2.6} />
        </span>
        <h3 className="text-base font-bold text-gray-900">{pominiete.length ? 'Gotowe na teraz' : 'Konto skonfigurowane'}</h3>
      </div>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">
        {stan.zrobione} z {stan.wszystkich} kroków za Tobą. Możesz dodawać oferty, planować montaże i projektować instalacje w 3D.
      </p>
      {pominiete.length > 0 && (
        <p className="mt-2 text-sm leading-relaxed text-gray-600">
          Pominięte: {pominiete.join(', ')}. Wrócisz do nich w menu konta, w pozycji Pierwsze kroki.
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <PrzyciskGlowny onClick={() => { zakoncz(); navigate('/pulpit') }}>Przejdź do pulpitu <ArrowRightIcon className="h-4 w-4" /></PrzyciskGlowny>
        <PrzyciskDrugi onClick={zakoncz}>Zamknij</PrzyciskDrugi>
      </div>
    </div>
  )
}

const ListaKrokow = () => {
  const { stan, wybierzKrok } = useSamouczek()
  const biezacy = stan.biezacy?.krok.klucz
  const ETYKIETA = { zrobiony: 'gotowe', pominiety: 'pominięty', czeka: 'czeka', otwarty: '' }
  return (
    <ol className="border-t border-gray-100 px-2 py-2">
      {stan.kroki.map(({ krok, stan: s }, i) => {
        const Ikona = ikonaKroku(krok.klucz)
        return (
          <li key={krok.klucz}>
            <button type="button" onClick={() => wybierzKrok(krok.klucz)} aria-current={krok.klucz === biezacy ? 'step' : undefined}
                    className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-gray-50 ${krok.klucz === biezacy ? 'bg-blue-50' : ''}`}>
              <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg ${s === 'zrobiony' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {s === 'zrobiony' ? <CheckIcon className="h-4 w-4" strokeWidth={2.6} /> : <Ikona className="h-4 w-4" />}
              </span>
              <span className={`min-w-0 flex-1 truncate text-sm ${s === 'zrobiony' ? 'text-gray-500' : 'font-medium text-gray-800'}`}>{i + 1}. {krok.tytul}</span>
              {ETYKIETA[s] && <span className="flex-shrink-0 text-xs text-gray-400">{ETYKIETA[s]}</span>}
            </button>
          </li>
        )
      })}
    </ol>
  )
}

const PanelSamouczka = () => {
  const s = useSamouczek()
  const [lista, setLista] = useState(false)
  if (!s.widocznyPanel) return null

  const { stan, pamiec, podswietlenie } = s
  const zwiniety = pamiec.zwiniety || Boolean(podswietlenie) || s.wymuszoneZwiniecie
  const opisPostepu = `${stan.zrobione} z ${stan.wszystkich} gotowe`

  if (zwiniety) {
    const procent = stan.wszystkich ? Math.round((stan.zrobione / stan.wszystkich) * 100) : 0
    return (
      <button type="button" onClick={() => { s.zamknijPodswietlenie(); s.zwin(false) }}
              aria-label={`Pierwsze kroki, ${opisPostepu}. Rozwiń`}
              className="fixed bottom-4 right-4 z-[45] flex items-center gap-2.5 rounded-full border border-gray-200 bg-white py-1.5 pl-1.5 pr-4 shadow-lg hover:border-blue-300 print:hidden">
        <span className="relative flex h-9 w-9 items-center justify-center">
          <svg viewBox="0 0 36 36" className="absolute inset-0 h-9 w-9 -rotate-90" aria-hidden="true">
            <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" className="stroke-gray-200" />
            <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="3" strokeLinecap="round" className="stroke-blue-600"
                    strokeDasharray={`${(procent / 100) * 97.4} 97.4`} />
          </svg>
          <RocketLaunchIcon className="h-4 w-4 text-blue-600" aria-hidden="true" />
        </span>
        <span className="text-left">
          <span className="block text-sm font-semibold leading-tight text-gray-900">Pierwsze kroki</span>
          <span className="block text-xs leading-tight text-gray-500">{opisPostepu}</span>
        </span>
        <ChevronUpIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
      </button>
    )
  }

  return (
    <aside aria-label="Pierwsze kroki"
           className="fixed inset-x-3 bottom-3 z-[45] max-h-[78vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-2xl sm:inset-x-auto sm:bottom-5 sm:right-5 sm:w-[380px] print:hidden">
      <div className="px-4 pt-4">
        <div className="flex items-center gap-3">
          <RocketLaunchIcon className="h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-gray-900">Pierwsze kroki</p>
            <p className="text-xs text-gray-500">{opisPostepu}{stan.pominiete ? `, ${stan.pominiete} pominięte` : ''}</p>
          </div>
          <button type="button" onClick={() => s.zwin(true)} aria-label="Zwiń panel"
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <ChevronDownIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-3"><Postep kroki={stan.kroki} biezacy={stan.biezacy?.krok.klucz} /></div>
      </div>

      {stan.biezacy ? <Krok /> : <Podsumowanie />}

      {lista && <ListaKrokow />}
      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-2 py-1.5">
        <button type="button" onClick={() => setLista((l) => !l)} aria-expanded={lista}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">
          Wszystkie kroki {lista ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
        </button>
        {stan.biezacy && (
          <button type="button" onClick={s.pominCalosc} className="rounded-lg px-2 py-2 text-xs font-semibold text-gray-500 hover:bg-gray-50 hover:text-gray-800">
            Pomiń samouczek
          </button>
        )}
      </div>
    </aside>
  )
}

export default PanelSamouczka
